import { Suspense, useContext, useEffect, useState } from "react";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import {
  IDebitmanagment,
  NegotiationStatus,
  REPOSSESSION_STATUS,
} from "../../../types/repossession.type";
import { getDebitManagement } from "../../../common/services/debit-management.api";
import { AuthContext } from "../../../common/components/auth/authContext/AuthContext";
import { getCompanySigla } from "../../../common/helpers/company.helpers";
import PaymentArrangementModal from "../modal/paymentArrangementModal";
import DebtCollectionModal from "../modal/debtCollectionModal";
import { getCompanyId } from "../../../common/services/company.api";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { startRenterConversation } from "../../../common/services/renters.api";
import { useNavigate } from "react-router-dom";
import { useCompanyStore } from "@/common/states/store/Company";
import { canAccessReceivablesDashboard } from "../receivablesDashboardAccess";

const STATUS_LABELS: Record<REPOSSESSION_STATUS, string> = {
  [REPOSSESSION_STATUS.PENDING]: "Pendente",
  [REPOSSESSION_STATUS.COMPLETED]: "Concluído",
  [REPOSSESSION_STATUS.SUSPENDED]: "Suspenso",
  [REPOSSESSION_STATUS.CANCELED]: "Cancelado",
};

const HEADERS: Record<string, string> = {
  companyId: "Cidade",
  nameRenter: "Cliente",
  billingAgentName: "Responsável",
  whatsapp: "Whatsapp",
  licensePlate: "Placa do Veículo",
  status: "Status",
  daysOverdue: "Dias pendentes",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDataForTable = (data: IDebitmanagment[]): any[] => {
  return data.map((item) => ({
    companyId: item.companyId || "",
    nameRenter: item.renter?.name || "",
    renterId: item.renter?.id || "",
    whatsapp: item.renter?.whatsapp || "",
    licensePlate: item.renter?.licensePlate || "",
    billingAgentName: item.billingAgentName || item.billingAgentEmail || "Não atribuído",
    status: item.status || "",
    daysOverdue: item.daysOverdue || 0,
    _original: item,
  }));
};

export default function TableReceivablesManagement() {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const companyId = getCompanyId();

  const { notifyError } = useToastContext();
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
    itemsPerPage: "10",
  });
  const company = useCompanyStore();

  const [rows, setRows] = useState<IDebitmanagment[]>([]);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const EMPTY_ACTION = { eventName: "", data: undefined };
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const isAction = (eventName: string) => action.eventName === eventName;

  const updateItemInRows = (updatedItem: IDebitmanagment) => {
    setRows((currentRows) =>
      currentRows.map((item) =>
        item._id === updatedItem._id ? updatedItem : item
      )
    );
  };

  const actions = [
    {
      label: "Negociação",
      action: (data) =>
        setAction({ eventName: "editRenter", data: data as IDebitmanagment }),
      permission: "edit:leads",
    },
    {
      label: "Enviar para recolhimento",
      action: (data) => {
        const item = data as IDebitmanagment;
        if (item["_original"].renter?.vehicleId) {
          setAction({ eventName: "editMotoClub", data: item });
        } else {
          notifyError(
            "Não foi possível enviar para recolhimento: não há um veículo associado a este cliente."
          );
        }
      },
      permission: "edit:leads",
    },
  ];

  const loadReceivables = async () => {
    setIsLoading(true);

    const result = await getDebitManagement({
      ...filters,
      status: [NegotiationStatus.PENDING],
      page: page,
      itemsPerPage: Number(filters.itemsPerPage) || data.itemsPerPage,
    });

    setData({
      itemsPerPage: data.itemsPerPage,
      totalItems: result.totalItems,
    });

    if (!result.ok) {
      notifyError("Não foi possível carregar os recebíveis de cobrança.");
      setIsLoading(false);
      return;
    }

    setRows(result.data);
    setIsLoading(false);
  };

  useEffect(() => {
    loadReceivables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, companyId, page, company]);

  const columnRenderers = {
    companyId: (companyId: unknown) => getCompanySigla(String(companyId)) || "",
    whatsapp: (whatsapp: unknown, row: IDebitmanagment) => (
      <div>
        <a
          onClick={() => handleStartConversation(row._id?.toString() || "")}
          target="_blank"
          className="text-primary-600 hover:underline cursor-pointer"
          rel="noopener noreferrer"
        >
          {formatWhatsAppNumber(String(whatsapp))}
        </a>
      </div>
    ),
    nameRenter: (value: unknown, row: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowObj = (row as any)?._original ?? (row as any);
      const renterId = rowObj?.renter?.id ?? rowObj?.renterId;
      const renterName = String(value ?? rowObj?.renter?.name ?? "");
      if (!renterId) return renterName;
      return (
        <a
          href={`/clientes/${renterId}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/clientes/${renterId}`);
          }}
          className="hover:underline hover:text-bluePrimary"
          rel="noopener noreferrer"
        >
          {renterName}
        </a>
      );
    },
    billingAgentName: (value: unknown) => String(value || "Não atribuído"),
    licensePlate: (value: unknown) => String(value || ""),
    status: (value: unknown) =>
      STATUS_LABELS[value as REPOSSESSION_STATUS] || String(value),
    daysOverdue: (value: unknown) => String(value || 0),
  };

  function formatWhatsAppNumber(whatsapp: string) {
    const cleanedNumber = whatsapp.replace(/\D/g, "");
    if (cleanedNumber.length == 10) {
      return `(${cleanedNumber.slice(0, 2)}) 9${cleanedNumber.slice(
        2,
        7
      )}-${cleanedNumber.slice(7)}`;
    }
    if (cleanedNumber.length == 11) {
      return `(${cleanedNumber.slice(0, 2)}) ${cleanedNumber.slice(
        2,
        7
      )}-${cleanedNumber.slice(7)}`;
    }
  }

  const handleStartConversation = async (renterId: string) => {
    setIsLoading(true);
    const result = await startRenterConversation(renterId);
    if (result.ok) {
      setIsLoading(false);
      window.open(result.data, "_blank");
    }
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Gestão de Cobranças"
          placeholder="Digite o cliente ou a placa"
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          tableData={rows}
        />
        {canAccessReceivablesDashboard(auth?.user?.email) && (
          <div className="flex justify-start -mt-4 mb-6">
            <a
              href="/financeiro/gestao-cobranca/dashboard"
              onClick={(e) => {
                e.preventDefault();
                navigate("/financeiro/gestao-cobranca/dashboard");
              }}
              className="text-sm font-medium hover:underline"
              style={{ color: "rgb(228,65,49)" }}
            >
              Dashboard
            </a>
          </div>
        )}

        <GenericTable
          entity="debit-managment"
          headers={HEADERS}
          data={mapDataForTable(rows)}
          actions={actions}
          renders={{
            ...columnRenderers,
            whatsapp: (whatsapp, rows) => {
              const whatsappStr = String(whatsapp);
              const rowObj = rows as IDebitmanagment;
              return (
                <div>
                  <a
                    onClick={() =>
                      handleStartConversation(
                        rowObj["_original"].renter.id?.toString() || ""
                      )
                    }
                    target="_blank"
                    className="text-bluePrimary underline cursor-pointer"
                    rel="noopener noreferrer"
                  >
                    {formatWhatsAppNumber(whatsappStr)}
                  </a>
                </div>
              );
            },
          }}
          setFilters={({ orderBy }) => setFilters((f) => ({ ...f, orderBy }))}
          rowClassName={(row) =>
            row.status === REPOSSESSION_STATUS.PENDING
              ? "text-orange-700 font-medium"
              : ""
          }
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 0}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("editRenter") && (
          <PaymentArrangementModal
            initialIsOpen={true}
            onClose={() => setAction(EMPTY_ACTION)}
            data={action.data as IDebitmanagment}
            onUpdateInteraction={updateItemInRows}
          />
        )}

        {isAction("editMotoClub") && (
          <DebtCollectionModal
            initialIsOpen={true}
            onClose={() => setAction(EMPTY_ACTION)}
            data={action.data as IDebitmanagment}
          />
        )}
      </div>
    </Suspense>
  );
}
